import React, { useEffect, useRef, useState } from 'react';
import { supportApi, buildStaticUrl, type SupportMessage } from '../../services/supportApi';
import { supportSocket } from '../../services/supportSocket';
import { useAuthStore } from '../../stores/authStore';

interface SupportChatPanelProps {
  conversationId: string;
  onClose: () => void;
  /** 用户首次发出消息时回调（用于启动未读轮询） */
  onUserSent?: () => void;
}

/**
 * 非模态客服窗口：右下角悬浮，不遮挡背景（用户可继续浏览）。
 * 复用 supportApi / supportSocket 的收发逻辑。
 */
export const SupportChatPanel: React.FC<SupportChatPanelProps> = ({ conversationId, onClose, onUserSent }) => {
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [input, setInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuthStore();
  const userAvatar = user?.avatar_url ? buildStaticUrl(user.avatar_url) : '';
  const userInitial = (user?.nickname || 'U').charAt(0).toUpperCase();

  useEffect(() => {
    let mounted = true;
    const onMessage = (msg: SupportMessage) => {
      if (!mounted) return;
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
    };
    const onHistory = (data: { conversationId: string; messages: SupportMessage[] }) => {
      if (data.conversationId === conversationId && mounted) setMessages(data.messages);
    };

    supportApi.getMessages(conversationId).then((history) => {
      if (mounted) setMessages(history);
    });

    supportSocket.connect();
    supportSocket.onMessage(onMessage);
    supportSocket.onHistory(onHistory);
    supportSocket.join(conversationId); // whenConnected 内部保证连上后再 join

    return () => {
      mounted = false;
      // 只移除本组件监听，不断开 socket（避免 StrictMode 双挂断连导致发送失败）
      supportSocket.offMessage(onMessage);
      supportSocket.offHistory(onHistory);
    };
  }, [conversationId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    supportSocket.sendText(conversationId, text);
    setInput('');
    onUserSent?.();
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const imageUrl = await supportApi.uploadImage(file);
      if (imageUrl) { supportSocket.sendImage(conversationId, imageUrl); onUserSent?.(); }
    } catch {
      /* 忽略 */
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-2rem)] bg-white rounded-2xl shadow-2xl border border-primary-100 flex flex-col">
      {/* 头部 */}
      <div className="flex items-center justify-between px-4 h-12 border-b border-primary-100 bg-primary-50 rounded-t-2xl">
        <h3 className="text-sm font-semibold text-primary-800">Contact us</h3>
        <button onClick={onClose} className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-white/60">✕</button>
      </div>

      {/* 消息列表 */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {messages.map((m) => {
          const isUser = m.sender_role === 'user';
          const alignRight = !isUser; // 客服靠右、用户靠左（与 Modal 一致）
          const avatar = isUser ? (
            userAvatar ? (
              <img src={userAvatar} alt="User" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-gray-400 text-white text-xs flex items-center justify-center flex-shrink-0">{userInitial}</div>
            )
          ) : (
            <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 text-sm">🎧</div>
          );
          return (
            <div key={m.id} className={`flex items-end gap-2 ${alignRight ? 'justify-end' : 'justify-start'}`}>
              {!alignRight && avatar}
              <div className={`max-w-[70%] rounded-2xl px-3 py-2 text-sm ${alignRight ? 'bg-primary-500 text-white rounded-br-sm' : 'bg-gray-100 text-gray-900 rounded-bl-sm'}`}>
                {m.content_type === 'image' && m.image_url ? (
                  <img src={buildStaticUrl(m.image_url)} alt="Image message" className="rounded-lg max-w-full max-h-52 object-contain" />
                ) : (
                  <span className="whitespace-pre-wrap break-words">{m.content}</span>
                )}
              </div>
              {alignRight && avatar}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* 输入区 */}
      <div className="flex items-center gap-2 px-3 h-14 border-t border-primary-100">
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="p-2 rounded-md text-gray-500 hover:bg-primary-50 disabled:opacity-50" title="发送图片">🖼</button>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder="Type a message…"
          className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <button onClick={handleSend} className="px-3 py-2 rounded-md bg-primary-500 text-white hover:bg-primary-600 text-sm">Send</button>
      </div>
    </div>
  );
};
